import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { TRAVEL_TAG_ICONS, getTagIconId, type TagIconId } from '../../utils/tagIcons';
import {
  MAX_TAG_COUNTRY_FLAGS,
  getTagCountryIso2List,
  setTagAppearance,
} from '../../utils/tagAppearance';
import { filterFlagCountries } from '../../utils/flagCountries';
import { TagColorChip } from './TagBadge';
import { CountryFlag } from '../stats/CountryFlag';

type Props = {
  tag: string;
  language?: string;
  onAppearanceChange?: () => void;
  compact?: boolean;
};

export const TagAppearancePanel: React.FC<Props> = ({
  tag,
  language = 'ru-RU',
  onAppearanceChange,
  compact = false,
}) => {
  const [flagQuery, setFlagQuery] = useState('');
  const useRu = language.toLowerCase().startsWith('ru');
  const trimmed = tag.trim();
  const currentColorId = getTagIconId(trimmed);
  const flags = getTagCountryIso2List(trimmed);

  const filteredCountries = useMemo(() => {
    const list = filterFlagCountries(flagQuery, language);
    return flagQuery.trim() ? list.slice(0, 60) : list.slice(0, 40);
  }, [flagQuery, language]);

  if (!trimmed) return null;

  const pickColor = (iconId: TagIconId) => {
    setTagAppearance(trimmed, { iconId });
    onAppearanceChange?.();
  };

  const toggleFlag = (code: string) => {
    const iso = code.toUpperCase();
    const next = flags.includes(iso)
      ? flags.filter((c) => c !== iso)
      : flags.length >= MAX_TAG_COUNTRY_FLAGS
        ? flags
        : [...flags, iso];
    setTagAppearance(trimmed, { countryIso2List: next });
    onAppearanceChange?.();
  };

  return (
    <div className={compact ? 'p-2' : 'p-2.5'}>
      <p className="mb-2 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500 dark:text-slate-400">
        {useRu ? 'Цвет тега' : 'Tag color'}
      </p>
      <div
        className={`grid grid-cols-6 gap-1.5 ${compact ? '' : 'max-h-40 overflow-y-auto overscroll-contain pr-0.5 gap-2'}`}
      >
        {TRAVEL_TAG_ICONS.map(({ id, labelRu, labelEn }) => {
          const active = id === currentColorId;
          return (
            <button
              key={id}
              type="button"
              title={useRu ? labelRu : labelEn}
              onClick={() => pickColor(id)}
              className={`flex items-center justify-center rounded-lg p-0.5 transition-all ${
                active
                  ? 'ring-2 ring-sky-500 ring-offset-1 scale-105 dark:ring-offset-slate-900'
                  : 'hover:scale-105'
              }`}
            >
              <TagColorChip iconId={id} size="md" />
            </button>
          );
        })}
      </div>

      <div className="mt-3 border-t border-stone-100 pt-2 dark:border-slate-700">
        <div className="mb-1.5 flex items-center justify-between gap-2 px-0.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500 dark:text-slate-400">
            {useRu ? 'Флаги' : 'Flags'}
          </p>
          {flags.length > 0 ? (
            <button
              type="button"
              onClick={() => {
                setTagAppearance(trimmed, { countryIso2List: [] });
                onAppearanceChange?.();
              }}
              className="text-[10px] font-medium text-stone-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-300"
            >
              {useRu ? 'Очистить' : 'Clear'}
            </button>
          ) : null}
        </div>
        {flags.length > 0 ? (
          <div className="mb-2 flex flex-wrap gap-1">
            {flags.map((iso2) => (
              <button
                key={iso2}
                type="button"
                onClick={() => toggleFlag(iso2)}
                className="inline-flex items-center gap-1 rounded-md bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-700 hover:bg-stone-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                title={useRu ? 'Убрать' : 'Remove'}
              >
                <CountryFlag iso2={iso2} shape="circle" />
                {iso2}
                <X className="h-2.5 w-2.5 opacity-60" />
              </button>
            ))}
          </div>
        ) : null}
        <input
          type="search"
          value={flagQuery}
          onChange={(e) => setFlagQuery(e.target.value)}
          placeholder={useRu ? 'Страна…' : 'Country…'}
          className="mb-1.5 h-8 w-full rounded-lg border border-stone-200 bg-white px-2 text-xs dark:border-slate-600 dark:bg-slate-900"
        />
        <div className="max-h-36 space-y-0.5 overflow-y-auto overscroll-contain">
          {filteredCountries.map((c) => {
            const on = flags.includes(c.code);
            const full = !on && flags.length >= MAX_TAG_COUNTRY_FLAGS;
            return (
              <button
                key={c.code}
                type="button"
                disabled={full}
                onClick={() => toggleFlag(c.code)}
                className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-xs ${
                  on
                    ? 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-100'
                    : full
                      ? 'cursor-not-allowed opacity-40'
                      : 'hover:bg-stone-50 dark:hover:bg-slate-700/70'
                }`}
              >
                <CountryFlag iso2={c.code} shape="circle" />
                <span className="min-w-0 flex-1 truncate">{useRu ? c.ru : c.en}</span>
                <span className="shrink-0 text-[10px] text-stone-400">{c.code}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
